import { format } from 'date-fns';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, Calendar, ChevronLeft, ChevronRight, TrendingUp } from 'lucide-react';
import {
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceArea,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import {
	getCashMovementTerms,
	getForecastTerm,
} from "@/lib/terminology-config";
import { getCurrencySymbol } from "@/utils/currency";
import { useApp } from "../../contexts/AppContext";
import { usePermissions } from "../../hooks/usePermissions";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useForecastRunTransactions } from "../../hooks/useForecastRunTransactions";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { useUserPreferenceDefaults } from "../../contexts/UserPreferenceDefaultsContext";
import type { DateBreakdownTransaction } from "../../hooks/useDrillDown";
import {
	generateDateBreakdownDrillDown,
	useDrillDown,
} from '../../hooks/useDrillDown';
import { useFormatting } from '../../hooks/useFormatting';
import { useVisibleScenarios } from '../../hooks/useVisibleScenarios';
import { formatDate } from '../../lib/utils';
import { DrillDownModal } from '../modals/DrillDownModal';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
	BASE_ID,
	ClusterPickerPopover,
	SCENARIO_COLOURS,
	ScenarioOverlayPicker,
} from "./ScenarioOverlayPicker";
import type { ClusterCandidate } from "./ScenarioOverlayPicker";
import { useScenarioForecasts } from "../../hooks/useScenarioForecasts";
import { applyScenarioToForecast } from "../../lib/scenarioEngine";

const CHART_LINE_COLOR = 'var(--accent-sky-500)';
const CHART_THRESHOLD_COLOR = 'var(--mc-error-icon)';

type TimeRange = "7" | "30" | "90";

export const ForecastTimeline: React.FC = () => {
	const { mode, currentUser } = useApp();
	const { hasPermission } = usePermissions();
	const { formatCurrency } = useFormatting();
	const scenarios = useVisibleScenarios();
	const { drillDownData, isOpen, showDrillDown, closeDrillDown } =
		useDrillDown();
	const { getUserPreferences } = useUserPreferences();
	const { defaults } = useUserPreferenceDefaults();
	const userPrefs = getUserPreferences(currentUser.id);

	// Resolve health zone settings: user override → admin default
	const healthyThreshold = userPrefs.healthyThreshold ?? defaults.healthyThreshold;
	const cautionThreshold = userPrefs.cautionThreshold ?? defaults.cautionThreshold;
	const displayMode = userPrefs.chartHealthForecast90 ?? defaults.chartHealthForecast90;
	const showBands = displayMode === 'bands';
	const showLine = displayMode === 'bands' || displayMode === 'line';

	// Resolve default 90-day view: user override → admin default → "90"
	const resolvedDefaultView = userPrefs.defaultForecastView ?? defaults.defaultForecastView;
	const defaultTimeRange: TimeRange =
		resolvedDefaultView === '7-day' ? '7' :
		resolvedDefaultView === '30-day' ? '30' :
		'90';
	const [timeRange, setTimeRange] = useState<TimeRange>(defaultTimeRange);
	// Selected scenario IDs — BASE_ID is always the first option
	const [selectedIds, setSelectedIds] = useState<string[]>([BASE_ID]);
	const {
		baseForecast: liveBaseData,
		isLoading: isLoadingForecast,
		error: forecastError,
	} = useBaseForecast();

	const isLive = mode === "live";
	const nonBaseIds = selectedIds.filter((id) => id !== BASE_ID);
	const { data: scenarioForecastData } = useScenarioForecasts(
		nonBaseIds,
		"90d",
	);

	// Forecast run transactions for drill-down (live mode only)
	const { forecastRunId } = useBaseForecast();
	const {
		data: forecastRunTransactions,
		error: runTxnError,
	} = useForecastRunTransactions(forecastRunId, undefined, undefined, isLive);

	const getDaysCount = () => {
		switch (timeRange) {
			case "7":
				return 7;
			case "30":
				return 30;
			case "90":
			default:
				return 90;
		}
	};

	// Build the merged chart data: one row per day with a key per selected scenario
	const getFilteredData = () => {
		const days = getDaysCount();

		// Base line data — from shared context (handles demo/live switch)
		const baseSource = liveBaseData;

		const slicedBase = baseSource.slice(0, days + 1);

		// For each selected scenario that isn't __base__, pre-compute scenario data
		const scenarioDataMap: Record<string, { balance: number }[]> = {};
		for (const id of selectedIds) {
			if (id !== BASE_ID) {
				if (isLive) {
					const forecast = scenarioForecastData[id];
					if (forecast) {
						scenarioDataMap[id] = forecast.projectedForecast
							.map((p) => ({ balance: p.scenarioBalance }))
							.slice(0, days + 1);
					}
				} else {
					const scenario = scenarios.find((s) => s.id === id);
					if (scenario) {
						const result = applyScenarioToForecast(
							liveBaseData,
							scenario.changes,
						);
						scenarioDataMap[id] = result.projectedForecast
							.map((p) => ({ balance: p.scenarioBalance }))
							.slice(0, days + 1);
					}
				}
			}
		}

		return slicedBase.map((point, idx) => {
			const row: Record<string, unknown> = {
				date: point.date,
				dateStr: formatDate(point.date),
				[BASE_ID]: point.balance,
			};
			for (const [id, data] of Object.entries(scenarioDataMap)) {
				row[id] = data[idx]?.balance ?? null;
			}
			return row;
		});
	};

	const filteredData = getFilteredData();

	// ── Pill scroll state ──────────────────────────────────────────────────────
	const pillsScrollRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const el = pillsScrollRef.current;
		if (!el) return;
		setCanScrollLeft(el.scrollLeft > 0);
		setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
	}, []);

	useEffect(() => {
		updateScrollState();
	});

	const scrollPills = (dir: 'left' | 'right') => {
		const el = pillsScrollRef.current;
		if (!el) return;
		el.scrollBy({ left: dir === 'right' ? 120 : -120, behavior: 'smooth' });
	};

	const toggleScenario = (id: string) => {
		if (id === BASE_ID) {
			// Base can't be deselected if it's the only one
			if (selectedIds.length === 1 && selectedIds[0] === BASE_ID) return;
			setSelectedIds((prev) =>
				prev.includes(BASE_ID)
					? prev.filter((s) => s !== BASE_ID)
					: [BASE_ID, ...prev.filter((s) => s !== BASE_ID)],
			);
		} else {
			setSelectedIds((prev) =>
				prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
			);
		}
	};

	// Map each selected ID to a colour
	const colourFor = (id: string, selectionIndex: number) =>
		id === BASE_ID
			? CHART_LINE_COLOR
			: SCENARIO_COLOURS[selectionIndex % SCENARIO_COLOURS.length];

	// Build a label for a given ID — for chart tooltips and legends
	const labelFor = (id: string) => {
		if (id === BASE_ID) return `Base ${getForecastTerm(true)}`;
		const sc = scenarios.find((s) => s.id === id);
		if (!sc) return id;
		return `${sc.name} (${sc.status})`;
	};

	// ── Cluster picker state ─────────────────────────────────────────────────
	type ClusterStateType = {
		candidates: ClusterCandidate[];
		periodLabel: string;
		anchorX: number;
		anchorY: number;
		point: Record<string, unknown>;
		dataIndex: number;
	} | null;

	const [clusterState, setClusterState] = useState<ClusterStateType>(null);
	// Keep a ref so handleChartMouseMove always reads the live value (no stale closure)
	const clusterStateRef = useRef<ClusterStateType>(null);
	const setCluster = (val: ClusterStateType) => {
		clusterStateRef.current = val;
		setClusterState(val);
	};

	// Recharts v3: track the hovered index via onMouseMove and read on click.
	const activeIdxRef = useRef<number | null>(null);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChartMouseMove = (state: any) => {
		const raw = state?.activeTooltipIndex ?? state?.activeIndex;
		const idx = raw != null ? Number(raw) : null;
		activeIdxRef.current = idx;
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handlePointClick = (state: any, event?: React.MouseEvent) => {
		const idx = activeIdxRef.current;
		if (idx == null || Number.isNaN(idx) || !filteredData[idx]) return;
		const point = filteredData[idx];

		// Build candidate list from every selected series that has a value at this point
		const candidates: ClusterCandidate[] = selectedIds
			.filter((id) => typeof point[id] === "number")
			.map((id, selIdx) => ({
				id,
				label: labelFor(id),
				value: point[id] as number,
				colour: colourFor(
					id,
					selectedIds.indexOf(id) >= 0 ? selectedIds.indexOf(id) : selIdx,
				),
			}));

		if (candidates.length === 0) return;

		// Single series → fire directly, no picker needed
		if (candidates.length === 1) {
			triggerDrillDown(point, candidates[0].id);
			return;
		}

		// Multiple series → show cluster picker at click coordinates
		if (event) {
			setCluster({
				candidates,
				periodLabel: point.dateStr as string,
				anchorX: event.clientX,
				anchorY: event.clientY,
				point,
				dataIndex: idx,
			});
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const triggerDrillDown = (point: any, scenarioId: string = BASE_ID) => {
		const balance = point[scenarioId] as number;
		if (balance == null) return;

		// Derive incoming/outgoing from the base forecast data for this day index
		const dayIndex = filteredData.findIndex((d) => d.date === point.date);
		const sourceForecast = liveBaseData;
		const sourcePoint = sourceForecast[dayIndex];
		const incoming = sourcePoint?.incoming ?? 0;
		const outgoing = sourcePoint?.outgoing ?? 0;

		const scenarioName =
			scenarioId !== BASE_ID
				? scenarios.find((s) => s.id === scenarioId)?.name
				: undefined;

		const pointDate = new Date(point.date);
		const dateStr = format(pointDate, "MMMM dd, yyyy");

		// In live mode, filter forecast run transactions for this specific day
		let actualTransactions: DateBreakdownTransaction[] | undefined;
		if (isLive && runTxnError) {
			actualTransactions = [];
		} else if (isLive && forecastRunTransactions) {
			const dayStr = format(pointDate, "yyyy-MM-dd");
			actualTransactions = forecastRunTransactions
				.filter((txn) => format(new Date(txn.forecastDate), "yyyy-MM-dd") === dayStr)
				.map((txn) => ({
					name: txn.counterpartyName ?? "Unknown",
					description: txn.description,
					grossValueIn: txn.direction === "inflow" ? txn.outstandingAmount : 0,
					grossValueOut: txn.direction === "outflow" ? txn.outstandingAmount : 0,
					category: txn.category,
					dataSourceName: txn.dataSourceName ?? undefined,
				}));
		} else if (isLive) {
			actualTransactions = [];
		}

		const drillDown = generateDateBreakdownDrillDown(
			dateStr,
			balance,
			incoming,
			outgoing,
			pointDate,
			scenarioName,
			actualTransactions,
		);
		if (isLive && runTxnError) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Transaction Load Error", value: runTxnError instanceof Error ? runTxnError.message : "Failed to load transactions" },
			];
		} else if (isLive && !forecastRunId) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Note", value: "Run a forecast refresh to enable transaction drill-down" },
			];
		} else if (isLive && !forecastRunTransactions) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Note", value: "Loading transactions\u2026" },
			];
		}
		showDrillDown("forecast-point", drillDown);
	};

	if (!hasPermission('view_90day_forecast_screen')) return null;

	const CustomTooltip = ({
		active,
		payload,
	}: {
		active?: boolean;
		payload?: any[];
	}) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-popover text-popover-foreground border shadow-md rounded-md px-3 py-2 text-sm">
					<p className="font-semibold mb-1.5">
						{(payload[0]?.payload as any)?.dateStr}
					</p>
					{payload.filter((e: any) => e.type !== 'none').map((entry: any, index: number) => (
						<div key={index} className="flex items-center gap-2">
							<div
								className="w-2.5 h-2.5 rounded-full flex-shrink-0"
								style={{ backgroundColor: entry.color }}
							/>
							<span className="text-muted-foreground">{entry.name}:</span>
							<span className="font-semibold ml-auto pl-3">
								{formatCurrency(entry.value)}
							</span>
						</div>
					))}
				</div>
			);
		}
		return null;
	};

	// ── Metric card computations ────────────────────────────────────────────────
	const baseValues = filteredData.map((d) => (d[BASE_ID] as number) ?? 0);
	const lowestPoint = Math.min(...baseValues);
	const lowestDay = filteredData.findIndex((d) => (d[BASE_ID] as number) === lowestPoint);
	const averageBalance = Math.round(baseValues.reduce((sum, v) => sum + v, 0) / baseValues.length);
	const highestPoint = Math.max(...baseValues);
	const highestDay = filteredData.findIndex((d) => (d[BASE_ID] as number) === highestPoint);

	return (
		<div className="p-6">
			<DrillDownModal
				isOpen={isOpen}
				onClose={closeDrillDown}
				data={drillDownData}
			/>

			{clusterState && (
				<ClusterPickerPopover
					candidates={clusterState.candidates}
					periodLabel={clusterState.periodLabel}
					anchorX={clusterState.anchorX}
					anchorY={clusterState.anchorY}
					onSelect={(id) => triggerDrillDown(clusterState.point, id)}
					onDismiss={() => setCluster(null)}
				/>
			)}

			{/* ── Page title ───────────────────────────────────────────────────────── */}
			<h1 className="text-2xl font-bold text-gray-900 mb-6">90-Day View</h1>

			{/* ── Metric cards ─────────────────────────────────────────────────────── */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">

				{/* Lowest Point */}
				<Card className="h-full" style={{ borderLeft: `3px solid ${lowestPoint < cautionThreshold ? 'var(--mc-error-border)' : 'var(--mc-warning-border)'}` }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Lowest Point</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: lowestPoint < cautionThreshold ? 'var(--mc-error-bg)' : 'var(--mc-warning-bg)' }}>
								<AlertTriangle className="h-4 w-4" style={{ color: lowestPoint < cautionThreshold ? 'var(--mc-error-icon)' : 'var(--mc-warning-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(lowestPoint, 'full')}</div>
							<div className="text-xs text-neutral-500 mt-1">
								{lowestPoint < cautionThreshold ? '⚠ Below threshold — ' : ''}Day {lowestDay}
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Average Balance */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Average Balance</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								<Calendar className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(averageBalance, 'full')}</div>
							<div className="text-xs text-neutral-500 mt-1">Next {getDaysCount()} days (base)</div>
						</div>
					</CardContent>
				</Card>

				{/* Highest Point */}
				<Card className="h-full" style={{ borderLeft: '3px solid var(--mc-info-border)' }}>
					<CardContent className="p-4 flex flex-col justify-between h-full gap-4">
						<div className="flex items-start justify-between gap-2">
							<span className="text-base font-medium text-neutral-800">Highest Point</span>
							<div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
								style={{ backgroundColor: 'var(--mc-info-bg)' }}>
								<TrendingUp className="h-4 w-4" style={{ color: 'var(--mc-info-icon)' }} />
							</div>
						</div>
						<div>
							<div className="text-2xl font-bold text-neutral-900 tracking-tight">{formatCurrency(highestPoint, 'full')}</div>
							<div className="text-xs text-neutral-500 mt-1">Day {highestDay}</div>
						</div>
					</CardContent>
				</Card>

			</div>

			<Card>
				<div className="flex items-center justify-between flex-wrap gap-4 px-4 py-3 border-b">
					<div className="flex items-center gap-1">
						<CardTitle className="text-lg font-semibold text-neutral-900">
							Cash Flow {getForecastTerm(true)}
						</CardTitle>
						<InfoTooltip
							title={`Cash Flow ${getForecastTerm(true)}`}
							content="Your projected cash position over the selected period, based on expected income and outgoings from all connected data sources. Click any point on the chart to drill into that day's transactions."
						/>
					</div>
					<div className="flex items-center gap-4">
						<Tabs
							value={timeRange}
							onValueChange={(v) => setTimeRange(v as TimeRange)}
						>
							<TabsList>
								<TabsTrigger value="7">7 Days</TabsTrigger>
								<TabsTrigger value="30">30 Days</TabsTrigger>
								<TabsTrigger value="90">90 Days</TabsTrigger>
							</TabsList>
						</Tabs>
						<ScenarioOverlayPicker
							scenarios={scenarios}
							selectedIds={selectedIds}
							onToggle={toggleScenario}
						/>
						{selectedIds.filter(id => id !== BASE_ID).length > 0 && (
							<button
								type="button"
								onClick={() => setSelectedIds([BASE_ID])}
								className="text-xs text-muted-foreground hover:text-destructive transition-colors whitespace-nowrap"
							>
								Deselect all
							</button>
						)}
					</div>
				</div>

				{/* ── Active overlay pills — single scrollable row with arrow controls ── */}
				{selectedIds.filter(id => id !== BASE_ID).length > 0 && (
					<>
					<div className="relative border-b flex items-center">
						{/* Left arrow */}
						{canScrollLeft && (
							<button
								type="button"
								onClick={() => scrollPills('left')}
								className="flex-shrink-0 flex items-center justify-center w-7 h-full pl-2 text-muted-foreground hover:text-foreground transition-colors z-10"
								aria-label="Scroll left"
							>
								<ChevronLeft className="h-4 w-4" />
							</button>
						)}

						{/* Fade hint — left */}
						{canScrollLeft && (
							<div className="pointer-events-none absolute left-7 top-0 h-full w-6 bg-gradient-to-r from-background to-transparent z-10" />
						)}

						{/* Scrollable pills */}
						<div
							ref={pillsScrollRef}
							onScroll={updateScrollState}
							className="flex gap-2 px-4 py-2 overflow-x-auto scrollbar-none flex-1"
						>
							{selectedIds.filter(id => id !== BASE_ID).map((id, idx) => {
								const sc = scenarios.find(s => s.id === id);
								const label = sc ? sc.name : id;
								const colour = SCENARIO_COLOURS[(idx + 1) % SCENARIO_COLOURS.length];
								return (
									<Badge
										key={id}
										variant="outline"
										className="gap-1.5 cursor-pointer flex-shrink-0"
										onClick={() => toggleScenario(id)}
										style={{ borderColor: colour, color: colour }}
									>
										<span
											className="h-2 w-2 rounded-full flex-shrink-0"
											style={{ backgroundColor: colour }}
										/>
										{label}
										<span className="opacity-60">×</span>
									</Badge>
								);
							})}
						</div>

						{/* Fade hint — right */}
						{canScrollRight && (
							<div className="pointer-events-none absolute right-7 top-0 h-full w-6 bg-gradient-to-l from-background to-transparent z-10" />
						)}

						{/* Right arrow */}
						{canScrollRight && (
							<button
								type="button"
								onClick={() => scrollPills('right')}
								className="flex-shrink-0 flex items-center justify-center w-7 h-full pr-2 text-muted-foreground hover:text-foreground transition-colors z-10"
								aria-label="Scroll right"
							>
								<ChevronRight className="h-4 w-4" />
							</button>
						)}
					</div>

					</>
				)}

				<CardContent className="pt-4">


					{/* biome-ignore lint/a11y/useKeyWithClickEvents: chart background dismiss */}
					<div
						className="h-[400px] w-full outline-none"
						style={{ cursor: 'pointer' }}
						onMouseDown={(e) => {
							// Dismiss picker only when clicking chart background AND no modal is open
							if (clusterStateRef.current && !isOpen && (e.target as Element).closest('svg')) {
								setCluster(null);
							}
						}}
					>
						<ResponsiveContainer width="100%" height="100%">
							<ComposedChart
								data={filteredData}
								onClick={handlePointClick}
								onMouseMove={handleChartMouseMove}
								margin={{ top: 10, right: 30, left: 0, bottom: 0 }}

							>
								<CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
								<XAxis
									dataKey="dateStr"
									tick={{ fontSize: 12 }}
									interval={Math.floor(getDaysCount() / 6)}
								/>
								<YAxis
									tick={{ fontSize: 12 }}
									tickFormatter={(value) =>
										`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
									}
								/>
								<Tooltip content={clusterStateRef.current ? () => null : <CustomTooltip />} />

								{/* Health zone backgrounds — only when bands mode */}
								{showBands && (
									<>
										<ReferenceArea y1={healthyThreshold} y2={10000000} fill="var(--mc-success-bg)" fillOpacity={0.3} ifOverflow="hidden" />
										<ReferenceArea y1={cautionThreshold} y2={healthyThreshold} fill="var(--mc-warning-bg)" fillOpacity={0.3} ifOverflow="hidden" />
										<ReferenceArea y1={-10000000} y2={cautionThreshold} fill="var(--mc-error-bg)" fillOpacity={0.3} ifOverflow="hidden" />
									</>
								)}

								{/* Threshold line — shown for bands and line modes */}
								{showLine && (
									<ReferenceLine
										y={cautionThreshold}
										stroke={CHART_THRESHOLD_COLOR}
										strokeDasharray="5 5"
										strokeWidth={2}
										label={{
											value: 'Threshold',
											position: 'insideTopLeft',
											fill: CHART_THRESHOLD_COLOR,
											fontSize: 12,
										}}
									/>
								)}

								{/* Lines — base uses dashboard sky colour, scenarios use their palette colour */}
								{selectedIds.map((id, idx) => (
									<Line
										key={`line-${id}`}
										type="monotone"
										dataKey={id}
										stroke={id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx)}
										strokeWidth={id === BASE_ID ? 3 : 2}
										name={labelFor(id)}
										connectNulls={false}
										dot={false}
										activeDot={{
											r: 5,
											fill: id === BASE_ID ? CHART_LINE_COLOR : colourFor(id, idx),
											cursor: 'pointer',
										}}
									/>
								))}
							</ComposedChart>
						</ResponsiveContainer>
					</div>

					{/* Combined legend — below chart, centred */}
					<div className="mt-3 flex flex-wrap items-center justify-center gap-6 text-sm">
						{/* Base forecast line — always shown */}
						<div className="flex items-center gap-2">
							<div className="w-8 h-0.5 rounded-full" style={{ backgroundColor: CHART_LINE_COLOR }} />
							<span>Base Forecast</span>
						</div>
						{/* Threshold dashed line — shown for line/bands modes */}
						{showLine && (
							<div className="flex items-center gap-2">
								<div className="w-8 h-0 border-t-2 border-dashed" style={{ borderColor: CHART_THRESHOLD_COLOR }} />
								<span>Threshold ({getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K)</span>
							</div>
						)}
						{/* Divider + zone legend — only for bands mode */}
						{showBands && (
							<>
								<div className="w-px h-4 bg-border" />
								<div className="flex items-center gap-2">
									<div className="w-3 h-3 rounded" style={{ backgroundColor: "var(--mc-success-bg)", border: "1px solid var(--mc-success-icon)" }} />
									<span>Healthy (&gt;{getCurrencySymbol()}{(healthyThreshold / 1000).toFixed(0)}K)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-3 h-3 rounded" style={{ backgroundColor: "var(--mc-warning-bg)", border: "1px solid var(--mc-warning-icon)" }} />
									<span>Caution ({getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K–{getCurrencySymbol()}{(healthyThreshold / 1000).toFixed(0)}K)</span>
								</div>
								<div className="flex items-center gap-2">
									<div className="w-3 h-3 rounded" style={{ backgroundColor: "var(--mc-error-bg)", border: "1px solid var(--mc-error-icon)" }} />
									<span>Critical (&lt;{getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K)</span>
								</div>
							</>
						)}
					</div>

				</CardContent>
			</Card>
		</div>
	);
};
